/**
 * VoiceTranscriptButton — record a voice note and transcribe it to text (OpenAI Whisper
 * via promang-api, not TALAI). Field workers tap to record, tap to stop; the transcript
 * is handed back via onTranscript so it can be inserted into the composer body.
 *
 * Uses the browser MediaRecorder API. Large touch target, clear recording state.
 */
import React, { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MicrophoneIcon, StopIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { transcribeAudio } from "../../../api/statusUpdates";
import { TWclassNames } from "../../utils/Div";

const SUPPORTS_RECORDING =
  typeof window !== "undefined" &&
  typeof navigator !== "undefined" &&
  navigator.mediaDevices?.getUserMedia &&
  typeof window.MediaRecorder !== "undefined";

export function VoiceTranscriptButton({
  onTranscript,
  disabled = false,
  /** When true, start recording once (used by mobile Dikter entry). */
  autoStart = false,
  onAutoStartConsumed,
  /** Composer subject — forwarded so AI todo titles include where. */
  suggestContext = null,
}) {
  const { t } = useTranslation();
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const autoStartedRef = useRef(false);
  const mountedRef = useRef(true);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    if (!SUPPORTS_RECORDING) {
      toast.error(t("Opptak støttes ikke i denne nettleseren."));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mountedRef.current) {
        stream.getTracks?.().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        if (!blob.size) {
          if (mountedRef.current) setState("idle");
          return;
        }
        if (mountedRef.current) setState("transcribing");
        const ext = (recorder.mimeType || "audio/webm").includes("mp4") ? "m4a" : "webm";
        const res = await transcribeAudio(blob, {
          language: "no",
          filename: `voice-note.${ext}`,
          context: suggestContext,
        });
        if (!mountedRef.current) return;
        setState("idle");
        if (res?.text || res?.body_html) {
          onTranscript?.(res.text || "", {
            title: res.title || "",
            bodyHtml: res.body_html || "",
            suggestions: res.suggestions || null,
          });
        } else {
          toast.error(res?.errors?.[0] || t("Kunne ikke transkribere lyden."));
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setState("recording");
    } catch (err) {
      stopStream();
      if (mountedRef.current) setState("idle");
      toast.error(t("Fikk ikke tilgang til mikrofonen."));
    }
  }, [onTranscript, stopStream, t, suggestContext]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
    mediaRecorderRef.current = null;
  }, []);

  useEffect(() => {
    if (!autoStart || disabled || autoStartedRef.current) return;
    autoStartedRef.current = true;
    onAutoStartConsumed?.();
    startRecording();
  }, [autoStart, disabled, onAutoStartConsumed, startRecording]);

  useEffect(() => {
    if (!autoStart) autoStartedRef.current = false;
  }, [autoStart]);

  // Stop mic / recorder if the composer unmounts or mobile mode switches away.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const recorder = mediaRecorderRef.current;
      if (recorder) {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        if (recorder.state !== "inactive") {
          try {
            recorder.stop();
          } catch {
            // ignore stop races during teardown
          }
        }
        mediaRecorderRef.current = null;
      }
      stopStream();
    };
  }, [stopStream]);

  const handleClick = () => {
    if (disabled || state === "transcribing") return;
    if (state === "recording") stopRecording();
    else startRecording();
  };

  const isRecording = state === "recording";
  const isTranscribing = state === "transcribing";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isTranscribing}
      title={isRecording ? t("Stopp opptak") : t("Dikter oppdatering")}
      className={TWclassNames(
        "flex min-h-[40px] items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors",
        isRecording
          ? "animate-pulse bg-rose-600 text-white"
          : isTranscribing
          ? "cursor-not-allowed bg-slate-100 text-slate-400"
          : "text-slate-500 hover:bg-slate-100 hover:text-[#1463C7]"
      )}
    >
      {isRecording ? (
        <StopIcon className="h-5 w-5" />
      ) : (
        <MicrophoneIcon className="h-5 w-5" />
      )}
      <span className="hidden sm:inline">
        {isRecording ? t("Stopp") : isTranscribing ? t("Tolker…") : t("Dikter")}
      </span>
    </button>
  );
}

export default VoiceTranscriptButton;
